import { calculateGematriaWebCompatible, getAvailableCiphers, getCipherCategories } from './webCompatibleCalculator';
import { hebrewCiphers, processHebrewText, isHebrewText } from '../data/hebrewCiphers';
import { getCustomCiphers } from './customCipherStorage';

// Get all ciphers including custom ones
export const getAllCiphers = async (isHebrewMode = false) => {
  const customCiphers = await getCustomCiphers();
  
  if (isHebrewMode) {
    return { ...hebrewCiphers, ...customCiphers };
  }
  
  const builtInCiphers = getAvailableCiphers();
  return { ...builtInCiphers, ...customCiphers };
};

// Calculate gematria values for input text using selected ciphers
// Now uses the web-compatible calculator for exact web version compatibility
// Also supports Hebrew text with Hebrew ciphers and custom ciphers
export const calculateGematria = async (text, selectedCiphers, isHebrewMode = false) => {
  // If Hebrew mode is explicitly enabled or text contains Hebrew, use Hebrew calculator
  if (isHebrewMode || isHebrewText(text)) {
    return await calculateHebrewGematria(text, selectedCiphers, isHebrewMode);
  }
  
  // Get custom ciphers
  const allCustomCiphers = await getCustomCiphers();
  
  // Filter custom ciphers by language mode (only English ciphers for English mode)
  const customCiphers = {};
  Object.keys(allCustomCiphers).forEach(key => {
    const cipher = allCustomCiphers[key];
    // Only include English custom ciphers (isHebrewCipher === false)
    if (cipher.isHebrewCipher === false) {
      customCiphers[key] = cipher;
    }
  });
  
  // Calculate with built-in ciphers
  const builtInResults = calculateGematriaWebCompatible(text, selectedCiphers);
  
  // Calculate with custom ciphers
  const customResults = [];
  Object.keys(customCiphers).forEach(cipherKey => {
    const cipher = customCiphers[cipherKey];
    
    // Check if cipher is selected
    if (!selectedCiphers[cipherKey] && !selectedCiphers[cipher.name]) {
      return;
    }
    
    // Calculate using custom cipher
    const result = calculateWithCustomCipher(text, cipher);
    customResults.push({
      key: cipherKey,
      name: cipher.name,
      description: cipher.description,
      totalValue: result.totalValue,
      wordValues: result.wordValues,
      letterBreakdowns: result.letterBreakdowns,
      category: 'Custom',
      isCustom: true
    });
  });
  
  return [...builtInResults, ...customResults];
};

// Calculate Hebrew gematria
const calculateHebrewGematria = async (text, selectedCiphers, isHebrewMode = false) => {
  const results = [];
  
  if (!text.trim()) {
    return results;
  }
  
  // Get custom ciphers
  const allCustomCiphers = await getCustomCiphers();
  
  // Filter custom ciphers by language mode (only Hebrew ciphers for Hebrew mode)
  const customCiphers = {};
  Object.keys(allCustomCiphers).forEach(key => {
    const cipher = allCustomCiphers[key];
    // Only include Hebrew custom ciphers (isHebrewCipher === true)
    if (cipher.isHebrewCipher === true) {
      customCiphers[key] = cipher;
    }
  });
  
  const allCiphers = { ...hebrewCiphers, ...customCiphers };
  
  // Process each cipher
  Object.keys(allCiphers).forEach(cipherKey => {
    const cipher = allCiphers[cipherKey];
    
    // Check if cipher is selected (by key or name)
    if (!selectedCiphers[cipherKey] && !selectedCiphers[cipher.name]) {
      return;
    }
    
    // Calculate using Hebrew cipher
    const { totalValue, wordValues, letterBreakdowns } = processHebrewText(text, cipher);
    
    results.push({
      key: cipherKey,
      name: cipher.name,
      nameHebrew: cipher.nameHebrew,
      description: cipher.description,
      totalValue: totalValue,
      wordValues: wordValues,
      letterBreakdowns: letterBreakdowns,
      category: cipher.category,
      isCustom: cipher.isCustom || false
    });
  });
  
  return results;
};

// Calculate with custom cipher
const calculateWithCustomCipher = (text, cipher) => {
  const words = text.trim().split(/\s+/);
  const wordValues = [];
  const letterBreakdowns = [];
  let totalValue = 0;

  words.forEach(word => {
    let wordValue = 0;
    const letters = [];

    for (const char of word) {
      const upperChar = char.toUpperCase();
      const value = cipher.values[upperChar] || cipher.values[char] || 0;
      wordValue += value;
      if (value > 0) {
        letters.push(`${char}=${value}`);
      }
    }

    if (wordValue > 0) {
      wordValues.push({ word, value: wordValue });
      letterBreakdowns.push(letters.join(' + '));
      totalValue += wordValue;
    }
  });

  return { totalValue, wordValues, letterBreakdowns };
};

// Get all ciphers with initial selection state (all selected by default)
export const getInitialCipherSelections = (isHebrewMode = false) => {
  const selections = {};

  if (isHebrewMode) {
    // Hebrew mode - select Hebrew ciphers
    Object.keys(hebrewCiphers).forEach(cipherKey => {
      selections[cipherKey] = true;
      selections[hebrewCiphers[cipherKey].name] = true;
    });
  } else {
    // English mode - get ciphers from the web-compatible system
    const availableCiphers = getAvailableCiphers();

    // Loop through all ciphers and set each to true (selecting all by default)
    for (const cipherKey in availableCiphers) {
      selections[cipherKey] = true; // Set all ciphers as selected
      // Also enable by cipher name for compatibility
      selections[availableCiphers[cipherKey].name] = true;
    }
  }

  return selections;
};

// Get Hebrew ciphers
export const getHebrewCiphers = () => {
  return hebrewCiphers;
};

// Get Hebrew cipher categories
export const getHebrewCipherCategories = () => {
  const { hebrewCipherCategories } = require('../data/hebrewCiphers');
  return hebrewCipherCategories;
};

// Filter ciphers by search term
export const filterCiphersBySearchTerm = (searchTerm) => {
  const filteredCiphers = {};

  // Get ciphers from the web-compatible system
  const availableCiphers = getAvailableCiphers();

  for (const cipherKey in availableCiphers) {
    const cipher = availableCiphers[cipherKey];
    if (cipher.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      filteredCiphers[cipherKey] = cipher;
    }
  }

  return filteredCiphers;
};

// Export additional functions from web-compatible calculator
export { getAvailableCiphers, getCipherCategories };
